/******/ (() => { // webpackBootstrap
/* eslint-disable */
if (!customElements.get('product-card-with-variants')) {
  customElements.define('product-card-with-variants', class ProductCardWithVariants extends HTMLElement {
    constructor() {
      super();
      this.cacheDOMElements();
      this.bindEventHandlers();
      this.wethemeGlobal = document.querySelector('script#wetheme-global');
      this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
      this.cartType = 'drawer';
      this.cartAction = 'go_to_or_open_cart';
      this.isProcessingVariantChange = false;
    }

    cacheDOMElements() {
      /* ===== We need to cache the DOM elements in the constructor because the connectedCallback is called every time the element 
      is appended into the DOM ===== */
      this.sectionId = this.getAttribute('data-section-id');
      this.allowSoldoutSelection = this.getAttribute('data-allow-soldout-selection') === 'true';
      this.form = this.querySelector('form');
      this.variantInputs = this.querySelectorAll('product-swatch input[type="radio"]');
      this.variantIdInput = this.querySelector('form input[name="id"]');
      this.addToCartButton = this.form?.querySelector('.quick-add-button');

      const variantsScript = this.querySelector('script.variants');
      this.variantsData = variantsScript ? JSON.parse(variantsScript.textContent) : { variants: [] };
      this.normalizeVariantPayload();
      this.variantJsonById = new Map();
      this.collectInitialVariantJson();
      this.optionNames = this.collectOptionNames();
      this.productUrl = this.querySelector('.info a[href]')?.getAttribute('href') || '';
      // Liquid resolves combined listing values against sibling products; keep that before refreshes overwrite it.
      this.querySelectorAll('.swatch-element[data-option]').forEach((element) => {
        element.dataset.serverAvailable = element.getAttribute('data-swatch-option-available') || 'false';
      });
      /*
      this.input = this.querySelector('input');
      this.buttonMinus = this.querySelector("button[name='minus']");
      this.buttonPlus = this.querySelector("button[name='plus']");
      */
    }

    bindEventHandlers() {
      /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
      this.attachEventListeners = this.attachEventListeners.bind(this);
      this.detachEventListeners = this.detachEventListeners.bind(this);
    }

    connectedCallback() {
      this.attachEventListeners();
      // Align the CTA with the initial selection when the element is first connected.
      const initiallyChecked = this.querySelector('product-swatch input[type="radio"]:checked');
      const initialVariant = this.findVariantByOptions(this.collectSelectedOptions());
      if (initiallyChecked) this.updateButtonFromInput(initiallyChecked, initialVariant);
      this.refreshSwatchAvailability();
    }

    attachEventListeners() {
      /* ===== Attach the event listeners to the DOM elements ===== */
      this.variantInputs.forEach((swatchInput) => {
        swatchInput.addEventListener('change', this.onSwatchChange)
      });

      this.form.addEventListener('submit', this.onSubmit);

      if (window?.eventBus) {
        window.eventBus.on('variant:change', this.onVariantChange);
      }
    }

    onSwatchChange = (event) => {
      if (event.target?.disabled && !this.allowSoldoutSelection) return;
      // In the sticky CTA, let the main product form drive the change so it swaps content and URL without a reload.
      const mirroredToMain = !this.isSyncingFromMain && this.mirrorSelectionToMainProduct(event.target);
      // Combined listing values belong to a sibling product this form can't buy; the main form swaps this CTA for it.
      if (this.isSiblingListingInput(event.target)) {
        if (!mirroredToMain) window.location.href = event.target.getAttribute('data-product-fetch-url');
        return;
      }
      // Allow the underlying product-swatch component to handle the event as well.

      // Get all selected options for this product
      const selectedOptions = this.collectSelectedOptions();

      // Find the variant that matches the selected options
      const matchingVariant = this.findVariantByOptions(selectedOptions);

      if (matchingVariant) {
        this.variantIdInput.value = matchingVariant.id;
      } else {
        this.variantIdInput.value = '';
        console.warn('No matching variant found for the selected options');
      }

      // Mirrors the quick add CTA with the availability of the newly selected variant.
      this.updateButtonFromInput(event.target, matchingVariant);
      this.refreshSwatchAvailability(selectedOptions);
    }

    onVariantChange = (context = {}) => {
      if (this.isProcessingVariantChange) return;
      if (!context) return;
      if (context.sectionId && this.sectionId && context.sectionId !== this.sectionId) return;

      this.isProcessingVariantChange = true;

      try {
        const incomingVariant = context.variant || null;
        if (incomingVariant?.id) {
          this.cacheVariantJson(incomingVariant);
        }

        const normalizedVariant = incomingVariant?.id ? this.findVariantById(incomingVariant.id) : null;
        const locallySelectedOptions = this.collectSelectedOptions();
        const localMatchingVariant = this.findVariantByOptions(locallySelectedOptions);

        // For same-section events, trust the current UI selection over any representative variant payload.
        const optionSelection = localMatchingVariant
          ? locallySelectedOptions
          : this.resolveOptionSelection(normalizedVariant, incomingVariant);

        if (!optionSelection) {
          if (this.variantIdInput) {
            this.variantIdInput.value = '';
          }
          this.updateAddToCartButtonState(false);
          this.refreshSwatchAvailability();
          return;
        }

        this.syncSwatchSelections(optionSelection);

        const matchingVariant = localMatchingVariant || this.findVariantByOptions(optionSelection) || normalizedVariant || null;

        if (matchingVariant?.id && this.variantIdInput) {
          this.variantIdInput.value = matchingVariant.id;
        }

        const activeInput = this.querySelector('product-swatch input[type="radio"]:checked');
        if (activeInput) {
          this.updateButtonFromInput(activeInput, matchingVariant);
        } else {
          const isAvailable = Boolean(matchingVariant && matchingVariant.available && matchingVariant.force_out_of_stock !== true);
          this.updateAddToCartButtonState(isAvailable);
        }

        this.refreshSwatchAvailability(optionSelection);
      } finally {
        this.isProcessingVariantChange = false;
      }
    }

    onSubmit = async (e) => {
      e.preventDefault();
      const currentForm = e.currentTarget;
      const currentButton = currentForm.querySelector('.quick-add-button');
      const productWrapper = currentForm.closest('.quick-add-wrapper');

      if (!productWrapper) {
        console.error('Product wrapper not found.');
        return false;
      }

      // Retrieve liveRegion
      const liveRegion = currentForm.querySelector('.sr-only[aria-live="polite"]');

      this.isDesktopQuickAdd = currentButton.classList.contains('quick-add-button-desktop');

      // Loading spinner.
      if (this.isDesktopQuickAdd) {
        currentButton.classList.add('is-loading');
        currentButton.innerHTML = `<svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1.6em" height="1.6em" class="spin flex-full"><g clip-path="url(#clip0_3605_47041)"><path d="M12.5 23C6.42487 23 1.5 18.0751 1.5 12C1.5 5.92487 6.42487 1 12.5 1C18.5751 1 23.5 5.92487 23.5 12C23.5 15.1767 22.1534 18.0388 20 20.0468" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></g><defs><clipPath id="clip0_3605_47041"><rect width="24" height="24" fill="none" transform="translate(0.5)"/></clipPath></defs>${ this.translationsObject.translations.loading }</svg>`;
      }

      try {
        const formData = new FormData(currentForm);
        const data = new URLSearchParams(formData).toString();
        const response = await window.fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: data,
        });
        if (!response.ok) {
          return false;
        }
        
        // Update cart drawer
        if(this.cartType == 'drawer') {
          const responseJson = await response.json();
          window.eventBus.emit('update:cart:drawer', responseJson);
        }
  
        const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
        const response2 = await window.fetch(`${languageParam}/cart?view=compare`);
        if (!response2.ok) {
          return false;
        }
        const cart = await response2.json();
  
        if(this.cartType == 'drawer' && this.cartAction == 'go_to_or_open_cart') {
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        }
        else {
          if (this.isDesktopQuickAdd) {
            currentButton.classList.remove('is-loading');
            currentButton.innerHTML = this.translationsObject.translations.productAdded;
          }
        }
  
        if (this.cartType == 'drawer') {
          const successMessage = productWrapper.getAttribute('data-product-added');
          if (liveRegion && successMessage) {
            liveRegion.textContent = successMessage;
          }
        }
        
        const event = new CustomEvent('wetheme-cart-update', { detail: { cart } });
        document.documentElement.dispatchEvent(event);
        if (this.closest('related-product-pdp')) window.eventBus.emit('cart:added', { sectionId: this.sectionId, success: true, productURL: this.productUrl });
  
        if (this.isDesktopQuickAdd) {
          setTimeout(() => {
            const translationKey = currentButton.dataset.addToCartTranslationKey || 'addToCart';
            currentButton.classList.remove('is-loading');
            currentButton.innerHTML = this.translationsObject.translations[translationKey];
          }, 2000);
        }
  
      } catch (e) {
        console.error('Unable to add to cart: ', e);
      }

      return false;
    }

    disconnectedCallback() {
      /* ===== Detach the event listeners when the element is removed from the DOM ===== */
      this.detachEventListeners();
    }

    detachEventListeners() {
      /* ===== Detach the event listeners from the DOM elements ===== */
      this.variantInputs.forEach((swatchInput) => {
        swatchInput.removeEventListener('change', this.onSwatchChange)
      });

      this.form.removeEventListener('submit', this.onSubmit);

      if (window?.eventBus) {
        window.eventBus.off('variant:change', this.onVariantChange);
      }
    }

    // Inspect the selected swatch input and toggle the quick-add CTA accordingly.
    collectSelectedOptions() {
      const selectedOptions = {};
      this.querySelectorAll('product-swatch input[type="radio"]:checked').forEach((input) => {
        const swatchElement = input.closest('.swatch-element');
        if (!swatchElement) return;
        const optName = swatchElement.getAttribute('data-option');
        if (!optName) return;
        selectedOptions[optName] = input.getAttribute('value');
      });
      return selectedOptions;
    }

    normalizeVariantPayload() {
      if (!this.variantsData?.variants?.length) return;

      this.variantsData.variants = this.variantsData.variants.map((variant) => {
        const normalized = Object.assign({}, variant);
        normalized.available = variant?.available !== false;
        const forced = variant?.force_out_of_stock;
        normalized.force_out_of_stock = forced === true || forced === 'true';
        return normalized;
      });
    }

    collectInitialVariantJson() {
      const scripts = this.querySelectorAll('script[type="application/json"][data-resource]');
      scripts.forEach((script) => {
        try {
          const data = JSON.parse(script.textContent);
          if (data?.id) {
            this.variantJsonById.set(String(data.id), data);
          }
        } catch (error) {
          console.warn('Unable to parse variant JSON for product card swatch', error);
        }
      });
    }

    collectOptionNames() {
      const optionElements = Array.from(this.querySelectorAll('.swatch-element[data-option]'));
      if (!optionElements.length) return [];
      const names = new Set();
      optionElements.forEach((element) => {
        const name = element.getAttribute('data-option');
        if (name) names.add(name);
      });
      return Array.from(names);
    }

    findVariantByOptions(optionSelection = {}) {
      if (!this.variantsData?.variants?.length) return null;
      const selectionEntries = Object.entries(optionSelection);
      if (!selectionEntries.length) return null;

      return this.variantsData.variants.find((variant) => {
        return selectionEntries.every(([key, value]) => variant.options[key] === value);
      }) || null;
    }

    getMainProductInformation() {
      const sectionId = this.closest('.sticky-product-cta')?.getAttribute('data-sticky-cta-section-id');
      return sectionId ? document.querySelector(`product-information[data-section-id="${sectionId}"]`) : null;
    }

    mirrorSelectionToMainProduct(input) {
      const mainProduct = this.getMainProductInformation();
      const optionName = input?.closest('.swatch-element')?.getAttribute('data-option');
      if (!mainProduct || !optionName) return false;

      const mainInput = Array.from(mainProduct.querySelectorAll('.swatch-element[data-option] input[type="radio"]')).find(
        (candidate) => candidate.closest('.swatch-element').getAttribute('data-option') === optionName && candidate.value === input.value
      );
      if (!mainInput) return false;

      if (!mainInput.checked) mainInput.click();
      return true;
    }

    // Called by the main product form so this CTA reflects the selection made there.
    syncSelectionFromMain(optionSelection = {}) {
      const changedInputs = [];
      Object.entries(optionSelection).forEach(([optionName, value]) => {
        const input = Array.from(this.querySelectorAll('.swatch-element[data-option] input[type="radio"]')).find(
          (candidate) => candidate.closest('.swatch-element').getAttribute('data-option') === optionName && candidate.value === value
        );
        if (input && !input.checked) {
          input.checked = true;
          changedInputs.push(input);
        }
      });
      if (!changedInputs.length) return;

      this.isSyncingFromMain = true;
      try {
        changedInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
      } finally {
        this.isSyncingFromMain = false;
      }
    }

    isSiblingListingInput(input) {
      if (!input || input.getAttribute('data-is-combined-listing') !== 'true') return false;
      const productUrl = input.getAttribute('data-product-url');
      return Boolean(productUrl && this.productUrl && productUrl !== this.productUrl);
    }

    findVariantById(variantId) {
      if (!this.variantsData?.variants?.length || !variantId) return null;
      return this.variantsData.variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    refreshSwatchAvailability(baseSelection = null) {
      if (!this.variantsData?.variants?.length) return;
      const selection = baseSelection ? { ...baseSelection } : this.collectSelectedOptions();
      const selectedVariant = this.findVariantByOptions(selection);
      const selectedVariantId = selectedVariant?.id ? String(selectedVariant.id) : '';
      const swatchElements = this.querySelectorAll('.swatch-element[data-option]');

      swatchElements.forEach((element) => {
        const optionName = element.getAttribute('data-option');
        const input = element.querySelector('input[type="radio"]');
        if (!optionName || !input) return;

        if (this.isSiblingListingInput(input)) {
          this.updateSwatchElementAvailability(element, element.dataset.serverAvailable === 'true');
          return;
        }

        const candidateSelection = { ...selection, [optionName]: input.getAttribute('value') };
        const candidateVariant = this.findVariantByOptions(candidateSelection);
        let isForced = false;
        let isAvailable = false;

        if (candidateVariant) {
          isForced = candidateVariant.force_out_of_stock === true;
          isAvailable = candidateVariant.available && !isForced;
          const candidateId = String(candidateVariant.id);
          element.dataset.swatchVariantId = candidateId;
          element.setAttribute('data-swatch-variant-id', candidateId);
          element.dataset.selectedSwatchVariantId = selectedVariantId;
          element.setAttribute('data-selected-swatch-variant-id', selectedVariantId);
          this.updateVariantScriptForInput(input, candidateVariant);
        } else {
          isForced = input.getAttribute('data-force-out-of-stock') === 'true';
          element.removeAttribute('data-swatch-variant-id');
          element.removeAttribute('data-selected-swatch-variant-id');
        }

        input.dataset.forceOutOfStock = String(isForced);
        input.setAttribute('data-force-out-of-stock', String(isForced));
        const productSwatchEl = element.closest('product-swatch');
        const isNonColorSwatch = productSwatchEl?.getAttribute('data-is-color-swatch') !== 'true';
        const optionValue = input.getAttribute('value');
        const classAvailabilityOverride = isNonColorSwatch
          ? this.variantsData.variants.some(v => v.options[optionName] === optionValue && v.available && !v.force_out_of_stock)
          : null;
        this.updateSwatchElementAvailability(element, isAvailable, classAvailabilityOverride);
      });
    }

    cacheVariantJson(variant) {
      if (!variant?.id) return;
      this.variantJsonById.set(String(variant.id), variant);
    }

    resolveOptionSelection(normalizedVariant, incomingVariant) {
      if (normalizedVariant?.options && !Array.isArray(normalizedVariant.options)) {
        return { ...normalizedVariant.options };
      }

      const mapFromIncoming = this.buildOptionMap(incomingVariant);
      if (mapFromIncoming) return mapFromIncoming;

      const collected = this.collectSelectedOptions();
      return Object.keys(collected).length ? collected : null;
    }

    buildOptionMap(variant = null) {
      if (!variant) return null;
      if (variant.options && !Array.isArray(variant.options)) {
        return { ...variant.options };
      }

      if (!this.optionNames?.length) return null;

      const mapped = {};
      this.optionNames.forEach((name, index) => {
        const key = `option${index + 1}`;
        const fromArray = Array.isArray(variant.options) ? variant.options[index] : undefined;
        const value = fromArray || variant[key];
        if (typeof value === 'string' && value.length) {
          mapped[name] = value;
        }
      });

      return Object.keys(mapped).length ? mapped : null;
    }

    syncSwatchSelections(optionSelection) {
      if (!optionSelection) return;
      Object.entries(optionSelection).forEach(([optionName, selectedValue]) => {
        if (!optionName) return;
        const swatchElements = this.querySelectorAll(`.swatch-element[data-option="${optionName}"]`);
        swatchElements.forEach((element) => {
          const input = element.querySelector('input[type="radio"]');
          if (!input) return;
          const isMatch = input.getAttribute('value') === selectedValue;
          if (isMatch && !input.checked) {
            input.checked = true;
          }
          if (!isMatch && input.checked) {
            input.checked = false;
          }
          element.classList.toggle('active', isMatch);
        });
      });
    }

    updateVariantScriptForInput(input, candidateVariant) {
      if (!input || !candidateVariant?.id) return;
      const script = this.getVariantDataElement(input.id);
      if (!script) return;

      const cachedVariant = this.variantJsonById.get(String(candidateVariant.id));
      if (cachedVariant) {
        script.textContent = JSON.stringify(cachedVariant);
        this.variantJsonById.set(String(candidateVariant.id), cachedVariant);
        return;
      }

      const minimalPayload = {
        id: candidateVariant.id,
        available: candidateVariant.available,
        options: candidateVariant.options,
        force_out_of_stock: candidateVariant.force_out_of_stock === true
      };
      script.textContent = JSON.stringify(minimalPayload);
      this.variantJsonById.set(String(candidateVariant.id), minimalPayload);
    }

    updateSwatchElementAvailability(element, isAvailable, isAvailableForClass = null) {
      const availabilityValue = String(Boolean(isAvailable));
      element.setAttribute('data-swatch-option-available', availabilityValue);
      const classAvailability = isAvailableForClass !== null ? Boolean(isAvailableForClass) : Boolean(isAvailable);
      element.classList.toggle('available', classAvailability);
      element.classList.toggle('soldout', !classAvailability);

      const input = element.querySelector('input[type="radio"]');
      if (input) {
        if (!this.allowSoldoutSelection) {
          input.disabled = !Boolean(isAvailable);
          input.setAttribute('aria-disabled', String(!Boolean(isAvailable)));
        }
      }

      const statusText = element.querySelector('[data-status-text]');
      if (statusText) {
        if (!statusText.dataset.defaultText) {
          statusText.dataset.defaultText = statusText.textContent || '';
        }
        const soldOutLabel = this.translationsObject?.translations?.soldOut || 'Sold Out';
        statusText.textContent = isAvailable ? '' : statusText.dataset.defaultText || `(${soldOutLabel})`;
      }
    }

    updateButtonFromInput(input, matchingVariant = null) {
      if (!input) return;
      if (input.disabled && !this.allowSoldoutSelection) {
        this.updateAddToCartButtonState(false);
        return;
      }
      const variantData = matchingVariant || null;
      const forceOutOfStock = matchingVariant?.force_out_of_stock === true || (!matchingVariant && input.getAttribute('data-force-out-of-stock') === 'true');
      const isAvailable = Boolean(variantData && variantData.available !== false && !forceOutOfStock);

      this.updateAddToCartButtonState(isAvailable);
    }

    // Toggle text, accessibility attributes, and disabled state for the quick add button.
    updateAddToCartButtonState(isAvailable) {
      if (!this.addToCartButton) return;
      if (!this.addToCartButton.dataset.originalText) {
        this.addToCartButton.dataset.originalText = this.addToCartButton.textContent.trim();
      }

      const defaultKey = this.addToCartButton.dataset.addToCartTranslationKey || 'addToCart';
      const defaultLabel = this.translationsObject?.translations?.[defaultKey] || this.addToCartButton.dataset.originalText;
      const soldOutLabel = this.translationsObject?.translations?.soldOut || 'Sold Out';

      if (isAvailable) {
        this.addToCartButton.textContent = defaultLabel;
        this.addToCartButton.removeAttribute('disabled');
        this.addToCartButton.removeAttribute('aria-disabled');
        this.addToCartButton.classList.remove('sold-out');
        this.addToCartButton.setAttribute('aria-label', defaultLabel);
      } else {
        this.addToCartButton.textContent = soldOutLabel;
        this.addToCartButton.setAttribute('disabled', 'disabled');
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.classList.add('sold-out');
        this.addToCartButton.setAttribute('aria-label', soldOutLabel);
      }
    }

    // Leverage the embedded variant JSON emitted by the swatch snippet.
    getVariantData(inputId) {
      const script = this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Unable to parse variant data for', inputId, error);
        return null;
      }
    }

    getVariantDataElement(inputId) {
      if (!inputId) return null;
      return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
    }
  });
}
/******/ })()
;
